import React from "react";
import { MessagesSquareIcon, GemIcon, HandshakeIcon, WalletIcon } from "lucide-react";
import { ButtonLink } from "../shared/Button";
import { SectionHeader } from "../shared/SectionHeader";
import { Reveal } from "../shared/Reveal";

// [Trust] [Branding] Symmetrical: two values | image | two values. Values sourced from oceansideliving.com.au/about-us.
// Illustrative still life (no generated people) until an approved owner photo exists.
// DEMO founder photo (AI-generated). Replace with an approved photo of Tommy Bramley and Justin Pike before launch.
const OWNER_IMAGE = "https://cdn.magicpatterns.com/patterns/generated-images/b59559a2-01d0-4085-afeb-21dcd0c2d447.jpg";
const leftValues = [{
  title: "Our own money on the line",
  text: "We invest in our own developments, so we understand every decision you face.",
  icon: WalletIcon
}, {
  title: "Communication-focused",
  text: "Clear, honest updates at every stage, from start to handover.",
  icon: MessagesSquareIcon
}];
const rightValues = [{
  title: "A small, hands-on team",
  text: "We carefully manage our workload so each home gets the attention it deserves.",
  icon: HandshakeIcon
}, {
  title: "Quality-driven",
  text: "Established trade and supplier networks delivering quality finishes.",
  icon: GemIcon
}];
type Value = (typeof leftValues)[number];
function ValueItem({
  value,
  align
}: {
  value: Value;
  align: "left" | "right";
}) {
  return <div className={`text-center ${align === "left" ? "lg:text-right" : "lg:text-left"}`} data-id="element-281" data-mp="22midhke4nqj0">
      <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white text-charcoal ring-1 ring-line ${align === "left" ? "lg:ml-auto lg:mr-0" : "lg:ml-0"}`} data-id="element-282" data-mp="1xlmad91lo8en">
        <value.icon className="h-5 w-5" aria-hidden="true" data-id="element-283" data-mp="1np9i8flpe2m0" />
      </span>
      <h3 className="mt-4 text-[20px] font-semibold tracking-tight" data-id="element-284" data-mp="3n1lo73g5ym0m">{value.title}</h3>
      <p className="mt-2 text-[16px] leading-relaxed text-slate" data-id="element-285" data-mp="3mz7xt7wfhxze">{value.text}</p>
    </div>;
}
export function OwnerStory() {
  return <section id="owners" aria-labelledby="owners-heading" className="bg-paper py-20 md:py-28" data-id="element-286" data-mp="1gd1ttyprpf4c">
      <div className="mx-auto max-w-site px-5 md:px-10" data-id="element-287" data-mp="1l4biox3hsef6">
        <SectionHeader id="owners-heading" title="We build with an owner’s mindset." intro="Our standard is simple: build homes we would be proud to own ourselves. We invest in our own developments, so we understand how much the decisions matter. We bring that same conviction to yours." data-id="element-288" data-mp="1l64ns7ygiql2" />

        <div className="mt-14 grid items-center gap-12 lg:grid-cols-[1fr_minmax(0,420px)_1fr] lg:gap-14" data-id="element-289" data-mp="242nwiw5cmch3">
          <div className="order-2 space-y-12 lg:order-1" data-id="element-290" data-mp="1q3o5jhud5lhr">
            {leftValues.map((value, index) => <Reveal key={value.title} delay={index * 0.05} data-id="element-291" data-mp="1u3yl9m7yjq9t" data-mpi={index}><ValueItem value={value} align="left" data-id="element-292" data-mp="1u6t4jaf8j51n" /></Reveal>)}
          </div>
          <Reveal className="order-1 lg:order-2" data-id="element-293" data-mp="emoe0ofubaos">
            <figure className="relative mx-auto aspect-[4/5] w-full max-w-[420px] overflow-hidden rounded-[28px]" data-id="element-294" data-mp="iq2x5nz2s76g">
              <img src={OWNER_IMAGE} alt="Two builders standing together on a Perth building site" loading="lazy" className="h-full w-full object-cover object-[center_20%]" data-id="element-295" data-mp="b50qv75rd2ml" />
              <figcaption className="absolute inset-x-4 bottom-4 rounded-2xl bg-paper/95 px-5 py-4 text-center" data-id="element-296" data-mp="l4rkz7qepfm5">
                <p className="font-heading text-[18px] font-semibold" data-id="element-297" data-mp="g4vft55z3i50">Tommy Bramley &amp; Justin Pike</p>
                <p className="text-[14px] text-slate" data-id="element-298" data-mp="3om003jtr21d6">Directors, Oceanside Living</p>
              </figcaption>
              <span className="absolute right-3 top-3 rounded-full bg-paper/90 px-2.5 py-1 text-[11px] font-medium text-slate" data-id="element-299" data-mp="3jn6ipgbcdq7q">Demo photo</span>
            </figure>
          </Reveal>
          <div className="order-3 space-y-12" data-id="element-300" data-mp="3tjrtkia1lvjh">
            {rightValues.map((value, index) => <Reveal key={value.title} delay={index * 0.05} data-id="element-301" data-mp="35tnqcc0rnxt0" data-mpi={index}><ValueItem value={value} align="right" data-id="element-302" data-mp="35schvywdm1vk" /></Reveal>)}
          </div>
        </div>

        <div className="mt-14 text-center" data-id="element-303" data-mp="30tjg33qgbzb8">
          <ButtonLink to="/about" variant="secondary" data-id="element-304" data-mp="244s6z4ugvpwo">Meet the people behind your build</ButtonLink>
        </div>
      </div>
    </section>;
}